import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from "@angular/common/http";
import {Observable, BehaviorSubject, ReplaySubject} from "rxjs";
import {LoadingService} from "../shared/services/loading.service";
import {DataSource} from "@angular/cdk/collections";
import {map} from 'rxjs/operators';

export interface SearchObject {
  search?: string,
  page?: number,
  page_size?: number,
  ordering?: string,
  object_id?: string | number;
  content_type?: any;
}

export interface Response {
  count: number,
  results: any[]
  content_type?: number
}

export abstract class BaseService {
  currentPage: number;
  count: number;
  dataChange: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  filter: SearchObject;
  datasource: BaseDataSource;
  content_type = new ReplaySubject();

  constructor(public base_url: string, public http: HttpClient, public loadingService: LoadingService) {
    this.datasource = new BaseDataSource(this);
    this.filter = {};
  }

  get data(): any[] {
    return this.dataChange.value;
  }

  getList(search_object: SearchObject): Observable<Response> {
    let url = `/${this.base_url}`;
    return this.http.get<Response>(url,
      {
        params: Object.entries(search_object).reduce((params, [key, value]) => params.set(key, value), new HttpParams())
      }
    ).pipe(
      map(response => {
        if (response.hasOwnProperty('content_type')) {
          this.content_type.next(response.content_type);
          this.content_type.complete();
        }
        return response;
      })
    );
  }

  get(id: string | number) {
    return this.http.get<any>(`/${this.base_url}/${id}`);
  }

  put(id: string | number, item: object) {
    return this.http.put(`/${this.base_url}/${id}`, item);
  }

  patch(id: string | number, item: object) {
    return this.http.patch(`/${this.base_url}/${id}`, item);
  }

  post(item: object): any {
    return this.http.post(`/${this.base_url}`, item).pipe(
      map(item => {
        const copiedData = this.data.slice();
        copiedData.push(item);
        this.dataChange.next(copiedData);
        return item;
      })
    );
  }

  options() {
    return this.http.options<any>(`/${this.base_url}`);
  }

  getItems() {
    this.loadingService.setLoading(true);
    return this.getList(this.filter).pipe(map(items => {
        this.currentPage = this.filter.page;
        this.count = items.count;
        this.dataChange.next(items.results);
        this.loadingService.setLoading(false);
      })
    );
  }

  getPage(event) {
    this.loadingService.setLoading(true);
    this.filter.page = event.pageIndex + 1;
    this.filter.page_size = event.pageSize;
    this.getItems()
      .subscribe(() => this.loadingService.setLoading(false));
  }

  runSearch() {
    this.loadingService.setLoading(true);
    this.filter.page = 1;
    this.getItems()
      .subscribe(() => this.loadingService.setLoading(false));
  }

  sortTable(event) {
    // this.loadingService.setLoading(true);
    this.filter.page = 1;
    let direction = event.direction === 'desc' ? '-' : '';
    this.filter.ordering = event.direction ? `${direction}${event.active}` : '';
    this.getItems()
      .subscribe(() => this.loadingService.setLoading(false));
  }

  delete(id: string | number) {
    return this.http.delete(`/${this.base_url}/${id}`)
      .pipe(
        map(() => {
          const copiedData = this.data.slice();
          const filteredData = copiedData.filter(item => item.id !== id);
          this.dataChange.next(filteredData);
        })
      );
  }

}

export class BaseDataSource extends DataSource<any> {
  constructor(private _projectDatabase: BaseService) {
    super();
  }

  connect(): Observable<any[]> {
    return this._projectDatabase.dataChange;
  }

  disconnect() {
  }
}
